import { createContext, useEffect, useRef, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthUserOrNull } from '@frontegg/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import runtimeEnv from '~/shared/utils/runtimeEnv';

import { AccountsResponse, TenantsResponse } from '../utils/types';
import { useXFetch } from '../utils/useXFetch';

export const TENANT_ID_STORAGE_KEY = 'tenantId';
export const CONFIG_REDIRECT_PENDING_KEY = 'configRedirectPending';

// Define AuthContextValue by combining auth state with tenant and account data
interface AuthContextValue {
  user: ReturnType<typeof useAuthUserOrNull>;

  // tenants?: TenantsResponse['tenants'];
  // activeTenant?: TenantsResponse['activeTenant'];
  accounts?: AccountsResponse['accounts'];
  // activeAccountId?: AccountsResponse['activeAccountId']; // accountRes?.activeAccountId does not update when a tab is changed
  // activeAccountName?: AccountsResponse['accounts'][number]['accountName'];
  activeTenantName?: string;
  activeTenantId?: string;

  error?: Error;
  loading?: boolean;

  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthContextProviderProps {
  children: ReactNode;
}

/**
 * AuthContext provider that wraps Frontegg's useAuth, useTenants, and useAccounts.
 * Handles account refresh logic and makes all auth + tenant + account data available via context.
 */
export function AuthContextProvider({ children }: AuthContextProviderProps) {
  const user = useAuthUserOrNull();
  const api = useXFetch();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    data: accountRes,
    isLoading: accountsLoading,
    error: accountsError,
  } = useQuery({
    queryKey: ['getAccounts'],
    queryFn: async () => await api<AccountsResponse>(`/api/v1/me/accounts`),
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const {
    data: tenantsRes,
    isLoading: tenantsLoading,
    error: tenantsError,
  } = useQuery({
    queryKey: ['getTenants'],
    queryFn: async () =>
      await api<TenantsResponse>(
        `${runtimeEnv.fronteggBaseUrl}/frontegg/identity/resources/users/v3/me/tenants`
      ),
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const isInitializedRef = useRef(false);
  const activeTenantIdRef = useRef<string | undefined>(undefined);

  const activeTenantId = tenantsRes?.activeTenant?.tenantId;
  const activeTenantName = tenantsRes?.activeTenant?.name;

  useEffect(() => {
    if (!activeTenantId) {
      return;
    }

    if (!isInitializedRef.current) {
      const storedTenantId = localStorage.getItem(TENANT_ID_STORAGE_KEY);

      // If localStorage has a different tenant ID, it means another tab changed it
      if (storedTenantId && storedTenantId !== activeTenantId) {
        queryClient.refetchQueries();
      } else {
        localStorage.setItem(TENANT_ID_STORAGE_KEY, activeTenantId);
      }

      const pathname = location.pathname;
      const isConfigNodeRoute =
        pathname.startsWith('/settings/configurations/') && pathname !== '/settings/configurations';
      if (isConfigNodeRoute && storedTenantId && storedTenantId !== activeTenantId) {
        navigate('/settings/configurations', { replace: true });
      }
      const redirectPending = sessionStorage.getItem(CONFIG_REDIRECT_PENDING_KEY) === '1';
      if (isConfigNodeRoute && redirectPending) {
        navigate('/settings/configurations', { replace: true });
        sessionStorage.removeItem(CONFIG_REDIRECT_PENDING_KEY);
      }

      activeTenantIdRef.current = activeTenantId;
      isInitializedRef.current = true;
      return;
    }

    // Handle local tenant changes (in current tab)
    const prev = activeTenantIdRef.current;
    if (prev && prev !== activeTenantId) {
      localStorage.setItem(TENANT_ID_STORAGE_KEY, activeTenantId);
      activeTenantIdRef.current = activeTenantId;

      queryClient.refetchQueries();

      const pathname = location.pathname;
      const isConfigNodeRoute =
        pathname.startsWith('/settings/configurations/') && pathname !== '/settings/configurations';
      if (isConfigNodeRoute) {
        navigate('/settings/configurations', { replace: true });
      }
    } else {
      const pathname = location.pathname;
      const isConfigNodeRoute =
        pathname.startsWith('/settings/configurations/') && pathname !== '/settings/configurations';
    }
  }, [activeTenantId, queryClient, location.pathname, navigate]);

  useEffect(() => {
    const handleStorageEvent = (event: StorageEvent) => {
      if (event.key !== TENANT_ID_STORAGE_KEY || event.storageArea !== localStorage) {
        return;
      }

      // Storage event only fires in other tabs, not the tab that made the change
      const newTenantId = event.newValue;
      const currentTenantId = activeTenantIdRef.current;

      if (newTenantId && newTenantId !== currentTenantId) {
        activeTenantIdRef.current = newTenantId;

        window.location.reload();
      }
    };

    window.addEventListener('storage', handleStorageEvent);

    return () => {
      window.removeEventListener('storage', handleStorageEvent);
    };
  }, [queryClient]);

  const value = {
    isAuthenticated: !!user,
    user,
    // ...tenantsRes,
    ...accountRes,
    // activeAccountName, // accountRes?.activeAccountId does not update when a tab is changed
    activeTenantName,
    activeTenantId,
    loading: accountsLoading || tenantsLoading,
    error: accountsError || tenantsError,
  } as AuthContextValue;

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export { AuthContext, type AuthContextValue };
